import { t } from '@lingui/macro';
import { EyeOff, BellOff, Archive } from 'lucide-react';
import { FC } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { User } from '../../models/User';
import { My } from '../../services/My';
import { MiniCallBadge } from '../components/CallInfoItems';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

type IRoomItem = { room: Room, roomCall: RoomCall | undefined, isSelected: boolean, nrOfNotifs: number, theOtherUser: User | undefined, meInTheCall: boolean, pathEnd: string, disabledPush: boolean };

// ... (imports)

const RoomItem: FC<IRoomItem> = ({ room, roomCall, isSelected, nrOfNotifs, theOtherUser, meInTheCall, pathEnd, disabledPush }) => {
  const history = useHistory();
  const { setIsMobileMenuOpen } = useAppUIContext();

  const isOneToOne = room.isOneToOne;
  const isPersonalRoom = room.visibility === 'personal';
  const roomName = isPersonalRoom ? "# " + t`My Room` : Room.getRoomNameWithSharp(room, My.user.id);
  const iconName = isPersonalRoom ? '' : room.profileImageUrl ? room.profileImageUrl : room.isOneToOne ? theOtherUser?.photoUrl : '';

  const handleClick = async () => {
    const targetPath = `/room/${room.id}${pathEnd}`;
    if (history.location.pathname !== targetPath) {
      history.replace(targetPath);
      // Close menu on mobile after navigation
      if (window.innerWidth < 1200) {
        setIsMobileMenuOpen(false);
      }
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 cursor-pointer select-none transition-all duration-200 rounded-[8px] mx-2 max-w-[calc(100%-16px)]",
        isSelected
          ? "bg-gray-100 font-bold text-gray-900"
          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
      )}
      onClick={handleClick}
    >
      <Avatar className={cn("w-9 h-9", isOneToOne ? "rounded-full" : "rounded-[10px] border-[1px] border-gray-200")}>
        {isPersonalRoom ? (
          <div className="w-full h-full bg-orange-100 flex items-center justify-center">
            <Archive className="w-5 h-5 text-orange-600" strokeWidth={2} />
          </div>
        ) : (
          <>
            <AvatarImage src={iconName ?? undefined} className="object-cover" />
            <AvatarFallback className={cn("bg-gray-200 text-gray-500 text-xs", isOneToOne ? "rounded-full" : "rounded-[10px]")}>
              {roomName.replace('# ', '').substring(0, 1)}
            </AvatarFallback>
          </>
        )}
      </Avatar>

      <div className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
        <div className={cn("truncate font-semibold text-sm", isSelected ? "text-gray-900" : "text-gray-900", Room.isArchivedOneToOne(room) && "text-gray-400 line-through")}>
          {roomName}
        </div>
        {room.visibility === 'unlisted' && <EyeOff className="w-3 h-3 text-gray-400 shrink-0" />}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {roomCall && RoomCall.isActive(roomCall) && (
          <span>{MiniCallBadge(roomCall.userIds.length, meInTheCall ? false : true)}</span>
        )}
        {disabledPush && <BellOff className="w-5 h-5 text-gray-400 shrink-0" />}
        {!!nrOfNotifs && (
          <Badge
            variant="notification"
            className={cn(disabledPush ? "bg-gray-400" : "")}
          >
            {nrOfNotifs}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default RoomItem;


